'use client'

import React, { useEffect, useRef, useState } from 'react'
import { useModalContext } from './ModalContext'
import { BodyPortal } from './BodyPortal'
import { Button } from './Button'
import '@/styles/modal.css'

// Mobil nav bar'ı gizlemek için açık modal sayacı
let _mobileModalCount = 0

export type ModalPosition = 'center' | 'left' | 'right' | 'inpopup' | 'pageIn'
export type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | 'full'

export interface ModalButton {
  label: string
  onClick: () => void
  variant?: 'primary' | 'secondary' | 'danger' | 'success' | 'link'
  disabled?: boolean
  icon?: string
  loading?: boolean
}

interface ModalProps {
  isOpen: boolean
  onClose: () => void
  title?: string
  description?: string
  children: React.ReactNode
  position?: ModalPosition
  size?: ModalSize
  // Codex note: Do not add footer buttons whose only job is closing the modal
  // ("Vazgeç", "İptal", etc.). Users close modals from the header X, overlay, or ESC.
  // Footer buttons are for the modal's actual action such as "Kaydet", "Ekle", "Sil".
  buttons?: ModalButton[]
  stickyFooter?: React.ReactNode // Altında sabit footer
  hideHeader?: boolean // Header'ı gizle
  noOverlay?: boolean // Overlay/backdrop'ı kaldır
  closeOnOutsideClick?: boolean // noOverlay modallarda dış tık ile kapansın mı
  closeOnEscape?: boolean // ESC ile kapansın mı
  contentClassName?: string // Optional extra class for modal content
  /** Force a specific z-index for the backdrop/content (use sparingly). */
  zIndexOverride?: number
}

let modalIdCounter = 0

export function Modal({
  isOpen,
  onClose,
  title,
  description,
  children,
  position = 'center',
  size = 'md',
  buttons = [],
  stickyFooter,
  hideHeader = false,
  noOverlay = false,
  closeOnOutsideClick = true,
  closeOnEscape = true,
  contentClassName,
  zIndexOverride
}: ModalProps) {
  const modalContext = useModalContext()
  const modalContextRef = useRef(modalContext)
  const modalIdRef = useRef<string>(`modal-${++modalIdCounter}`)
  const [zIndex, setZIndex] = useState(10000)
  const isRegisteredRef = useRef(false)
  const finalizeClosePendingRef = useRef(false)
  const [isClosing, setIsClosing] = useState(false)
  const [shouldRender, setShouldRender] = useState(isOpen)
  const [isOpening, setIsOpening] = useState(false)
  const [, forceUpdate] = useState({})

  // Handle opening/closing with animation
  useEffect(() => {
    if (isOpen) {
      setShouldRender(true)
      setIsClosing(false)
      setIsOpening(false)

      // Mobilde tüm pozisyonlar bottom sheet → double RAF ile animasyon garantisi
      // Desktop'ta center direkt açılır, diğerleri double RAF
      const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768
      if (position === 'center' && !isMobile) {
        setIsOpening(true)
      } else {
        // Double RAF to ensure DOM is ready and transition can start
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setIsOpening(true)
          })
        })
      }
    } else if (shouldRender) {
      setIsOpening(false)
      setIsClosing(true)
      const timer = setTimeout(() => {
        setShouldRender(false)
        setIsClosing(false)
      }, 300) // Match animation duration
      return () => clearTimeout(timer)
    }
  }, [isOpen, shouldRender, position])

  // Register/unregister modal for stacking (push offset, z-index, ESC key)
  // Unregister immediately when isOpen becomes false so sibling modals revert their push offset right away
  useEffect(() => {
    if (!modalContext) return

    if (isOpen && !isRegisteredRef.current) {
      modalContext.registerModal(modalIdRef.current, position, onClose)
      setZIndex(modalContext.getZIndex(modalIdRef.current))
      isRegisteredRef.current = true
      finalizeClosePendingRef.current = false
      return
    }

    if (!isOpen && isRegisteredRef.current) {
      modalContext.unregisterModal(modalIdRef.current)
      isRegisteredRef.current = false
      finalizeClosePendingRef.current = true
    }
  }, [isOpen, modalContext, onClose, position])

  // Always keep modalContextRef up to date so the unmount cleanup can use the latest context
  useEffect(() => { modalContextRef.current = modalContext })

  // Unmount cleanup: if the component is removed from the tree while still open/registered
  // (e.g. conditional render: {condition && <Modal isOpen={true}>}) unregister here so
  // sibling modals revert their push offset and scroll lock is released properly.
  useEffect(() => {
    const modalId = modalIdRef.current
    return () => {
      if (!modalContextRef.current) return
      if (isRegisteredRef.current) {
        modalContextRef.current.unregisterModal(modalId)
        modalContextRef.current.finalizeClose(modalId)
        isRegisteredRef.current = false
      } else if (finalizeClosePendingRef.current) {
        // Was unregistered via isOpen=false but close animation didn't complete before unmount
        modalContextRef.current.finalizeClose(modalId)
        finalizeClosePendingRef.current = false
      }
    }
  }, []) // empty deps = cleanup only on unmount

  // Release scroll lock only after close animation fully completes
  useEffect(() => {
    if (!modalContext || !finalizeClosePendingRef.current) return
    if (!shouldRender) {
      modalContext.finalizeClose(modalIdRef.current)
      finalizeClosePendingRef.current = false
    }
  }, [shouldRender, modalContext])

  // Force re-render when modal stack changes (for transform calculations)
  useEffect(() => {
    if (!modalContext || !isOpen) return
    forceUpdate({})
  }, [modalContext, modalContext?.modals.length, isOpen])

  // Force re-render when panel layout changes (submenu/sidebar collapse/expand)
  // so `getContentLeftOffset()` / topbar metrics and pageIn sizing update.
  useEffect(() => {
    if (!isOpen) return
    if (position !== 'pageIn' && position !== 'inpopup') return
    if (typeof window === 'undefined') return

    const selectors = [
      '.panel-sidebar',
      '.panel-submenu',
      '.panel-leftpanel',
      '.panel-topbar',
      '.panel-bottombar',
      // legacy fallbacks
      '.panel-leftbar',
    ]

    const resizeObserver = new ResizeObserver(() => {
      forceUpdate({})
    })

    const mutationObservers: MutationObserver[] = []
    const observedEls: Element[] = []

    const attach = () => {
      // (Re)attach observers to current elements
      for (const selector of selectors) {
        const el = document.querySelector(selector)
        if (!el) continue
        if (observedEls.includes(el)) continue

        observedEls.push(el)
        try {
          resizeObserver.observe(el as Element)
        } catch {
          // ignore
        }

        const mo = new MutationObserver(() => {
          forceUpdate({})
        })
        try {
          mo.observe(el, { attributes: true, attributeFilter: ['class', 'style'] })
          mutationObservers.push(mo)
        } catch {
          // ignore
        }
      }
    }

    // Watch DOM for submenu being added/removed
    const rootMo = new MutationObserver(() => {
      attach()
      forceUpdate({})
    })
    try {
      rootMo.observe(document.body, { childList: true, subtree: true })
      mutationObservers.push(rootMo)
    } catch {
      // ignore
    }

    const onResize = () => forceUpdate({})
    window.addEventListener('resize', onResize)
    attach()

    return () => {
      window.removeEventListener('resize', onResize)
      try {
        resizeObserver.disconnect()
      } catch {
        // ignore
      }
      for (const mo of mutationObservers) {
        try {
          mo.disconnect()
        } catch {
          // ignore
        }
      }
    }
  }, [isOpen, position])

  // Manage body overflow if no context (standalone mode)
  useEffect(() => {
    if (modalContext) return // Context handles this

    const body = document.body
    const root = document.documentElement
    const lockKey = 'modalScrollLockStandalone'
    const prevOverflowKey = 'modalPrevOverflowStandalone'
    const prevPaddingKey = 'modalPrevPaddingRightStandalone'

    if (isOpen) {
      if (body.dataset[lockKey] !== '1') {
        body.dataset[lockKey] = '1'
        body.dataset[prevOverflowKey] = body.style.overflow || ''
        body.dataset[prevPaddingKey] = body.style.paddingRight || ''
      }

      const scrollbarWidth = Math.max(0, window.innerWidth - root.clientWidth)
      body.style.overflow = 'hidden'
      body.style.paddingRight = scrollbarWidth > 0 ? `${scrollbarWidth}px` : (body.dataset[prevPaddingKey] || '')
    } else {
      if (body.dataset[lockKey] === '1') {
        body.style.overflow = body.dataset[prevOverflowKey] || ''
        body.style.paddingRight = body.dataset[prevPaddingKey] || ''
        delete body.dataset[lockKey]
        delete body.dataset[prevOverflowKey]
        delete body.dataset[prevPaddingKey]
      } else {
        body.style.overflow = ''
        body.style.paddingRight = ''
      }
    }

    return () => {
      try {
        if (body.dataset[lockKey] === '1') {
          body.style.overflow = body.dataset[prevOverflowKey] || ''
          body.style.paddingRight = body.dataset[prevPaddingKey] || ''
          delete body.dataset[lockKey]
          delete body.dataset[prevOverflowKey]
          delete body.dataset[prevPaddingKey]
        }
      } catch {
        // ignore
      }
    }
  }, [isOpen, modalContext])

  // ESC key - only close if this is the top modal (or standalone)
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (!closeOnEscape) return
      if (e.key === 'Escape' && isOpen) {
        // If no context, always close. If context exists, only close if top modal
        if (!modalContext || modalContext.isTopModal(modalIdRef.current)) {
          onClose()
        }
      }
    }

    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [closeOnEscape, isOpen, onClose, modalContext])

  // Quicklook/no-overlay modals should close when the user clicks elsewhere on
  // the page, while table row clicks stay available for row-to-row navigation.
  useEffect(() => {
    if (!isOpen || !noOverlay) return
    if (!closeOnOutsideClick) return
    if (typeof document === 'undefined') return

    const handleOutsidePointer = (event: MouseEvent | TouchEvent) => {
      if (modalContext && !modalContext.isTopModal(modalIdRef.current)) return
      const target = event.target as HTMLElement | null
      if (!target) return
      if (contentRef.current?.contains(target)) return
      if (target.closest('.data-table tbody tr')) return
      onClose()
    }

    document.addEventListener('mousedown', handleOutsidePointer, true)
    document.addEventListener('touchstart', handleOutsidePointer, true)
    return () => {
      document.removeEventListener('mousedown', handleOutsidePointer, true)
      document.removeEventListener('touchstart', handleOutsidePointer, true)
    }
  }, [closeOnOutsideClick, isOpen, noOverlay, onClose, modalContext])

  // Mobil bottom nav bar: modal açıkken gizle, kapanınca geri getir
  useEffect(() => {
    if (typeof window === 'undefined') return
    if (window.innerWidth > 768) return
    if (!isOpen) return

    _mobileModalCount++
    document.body.classList.add('mobile-modal-open')

    return () => {
      _mobileModalCount = Math.max(0, _mobileModalCount - 1)
      if (_mobileModalCount === 0) document.body.classList.remove('mobile-modal-open')
    }
  }, [isOpen])

  // Mobil bottom sheet: sürükle-bırak ile kapama
  const contentRef   = useRef<HTMLDivElement>(null)
  const dragStartY   = useRef<number | null>(null)
  const dragCurrentY = useRef(0)
  const isDragging   = useRef(false)

  const handleDragTouchStart = (e: React.TouchEvent) => {
    dragStartY.current   = e.touches[0].clientY
    dragCurrentY.current = 0
    isDragging.current   = true
    if (contentRef.current) contentRef.current.style.transition = 'none'
  }

  const handleDragTouchMove = (e: React.TouchEvent) => {
    if (!isDragging.current || dragStartY.current === null) return
    const dy = e.touches[0].clientY - dragStartY.current
    if (dy < 0) return
    dragCurrentY.current = dy
    if (contentRef.current) contentRef.current.style.transform = `translateY(${dy}px)`
  }

  const handleDragTouchEnd = () => {
    if (!isDragging.current) return
    isDragging.current = false
    const content = contentRef.current
    if (content) content.style.transition = ''
    if (dragCurrentY.current > 100) {
      if (content) content.style.transform = ''
      onClose()
    } else {
      if (content) content.style.transform = ''
    }
    dragStartY.current   = null
    dragCurrentY.current = 0
  }

  if (!shouldRender) return null

  // Calculate sidebar + submenu width for pageIn position
  const getContentLeftOffset = () => {
    if (typeof window === 'undefined') return 0
    if (position === 'inpopup') return 0
    
    // pageIn için panel-sidebar, panel-submenu ve panel-leftpanel'i hesapla
    if (position === 'pageIn') {
      const sidebar = document.querySelector('.panel-sidebar')
      const submenu = document.querySelector('.panel-submenu')
      const leftpanel = document.querySelector('.panel-leftpanel:not(.panel-leftpanel--collapsed)')
      
      // Sidebar genişliği (collapsed ise 60px, normal ise 220px)
      let sidebarWidth = 0
      if (sidebar) {
        sidebarWidth = (sidebar as HTMLElement).offsetWidth
      }
      
      // Submenu varsa genişliğini ekle (280px)
      let submenuWidth = 0
      if (submenu) {
        submenuWidth = (submenu as HTMLElement).offsetWidth
      }
      
      // LeftPanel varsa genişliğini ekle (240px)
      let leftpanelWidth = 0
      if (leftpanel) {
        leftpanelWidth = (leftpanel as HTMLElement).offsetWidth
      }
      
      return sidebarWidth + submenuWidth + leftpanelWidth
    }
    
    // Diğer pozisyonlar için eski mantık
    const sidebar = document.querySelector('.panel-leftbar')
    const submenu = document.querySelector('.panel-submenu')
    const sidebarWidth = sidebar ? (sidebar as HTMLElement).offsetWidth : 0
    const submenuWidth = submenu ? (submenu as HTMLElement).offsetWidth : 0
    return sidebarWidth + submenuWidth
  }

  // Calculate topbar height for pageIn position
  const getTopbarHeight = () => {
    if (typeof window === 'undefined') return 0
    if (position !== 'pageIn') return 0
    const topbar = document.querySelector('.panel-topbar')
    return topbar ? (topbar as HTMLElement).offsetHeight : 60
  }

  // Calculate bottombar height for pageIn position
  const getBottombarHeight = () => {
    if (typeof window === 'undefined') return 0
    if (position !== 'pageIn') return 0
    const bottombar = document.querySelector('.panel-bottombar')
    return bottombar ? (bottombar as HTMLElement).offsetHeight : 0
  }

  const backdropClasses = [
    'modal-backdrop',
    `position-${position}`,
    isOpening ? 'opening' : '',
    isOpening ? 'visible' : 'transparent',
    isClosing ? 'closing' : '',
    noOverlay ? 'no-overlay' : ''
  ].filter(Boolean).join(' ')

  const contentClasses = [
    'modal-content',
    contentClassName || '',
    `position-${position}`,
    `size-${size}`,
    isOpening ? 'opening' : '',
    isClosing ? 'closing' : ''
  ].filter(Boolean).join(' ')

  const backdropStyle: React.CSSProperties = {
    zIndex: zIndexOverride ?? (position === 'pageIn' ? 100 : zIndex) // pageIn için düşük z-index (sidebar arkasında)
  }

  if (position === 'inpopup') {
    backdropStyle.left = getContentLeftOffset()
  }

  if (position === 'pageIn') {
    backdropStyle.left = getContentLeftOffset()
    backdropStyle.top = getTopbarHeight()
    const bottombarH = getBottombarHeight()
    if (bottombarH > 0) {
      backdropStyle.bottom = bottombarH
    }
  }

  // Add offset for stacked modals
  const modalIndex = modalContext ? modalContext.getModalIndex(modalIdRef.current, position) : 0
  const totalModalsInPosition = modalContext ? modalContext.getModalCountByPosition(position) : 1
  const contentStyle: React.CSSProperties = {}

  // For pageIn full-screen modals, compute width dynamically based on current left offset
  // (sidebar + submenu + leftpanel), so collapsing panels immediately expands the modal.
  if (position === 'pageIn' && size === 'full') {
    const leftOffset = getContentLeftOffset()
    contentStyle.width = `calc(100vw - ${leftOffset}px)`
  }
  
  // Latest modal stays at 0, older modals are pushed to show their edge
  // Only count modals in the same position (left/right/center/inpopup/pageIn)
  if (isOpening && !isClosing && (position === 'left' || position === 'inpopup' || position === 'pageIn')) {
    const modalsOnTop = Math.max(0, totalModalsInPosition - 1 - modalIndex)
    contentStyle.transform = modalsOnTop > 0 ? `translateX(${modalsOnTop * 40}px)` : 'translateX(0px)'
  } else if (isOpening && !isClosing && position === 'right') {
    const modalsOnTop = Math.max(0, totalModalsInPosition - 1 - modalIndex)
    contentStyle.transform = modalsOnTop > 0 ? `translateX(-${modalsOnTop * 40}px)` : 'translateX(0px)'
  }

  return (
    <BodyPortal>
      <div
        className={backdropClasses}
        style={backdropStyle}
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            if (!modalContext || modalContext.isTopModal(modalIdRef.current)) {
              onClose()
            }
          }
        }}
      >
        <div className={contentClasses} style={contentStyle} ref={contentRef}>
          {/* Mobil drag handle */}
          <div
            className="modal-drag-handle"
            aria-hidden="true"
            onTouchStart={handleDragTouchStart}
            onTouchMove={handleDragTouchMove}
            onTouchEnd={handleDragTouchEnd}
          />
          {!hideHeader && (
            <div
              className="modal-header"
            >
              <div className="modal-header-content">
                <h2 className={`modal-title ${description ? 'has-description' : ''}`}>
                  {title}
                </h2>
                {description && (
                  <p className="modal-description">
                    {description}
                  </p>
                )}
              </div>
              <button
                onClick={onClose}
                onTouchStart={(e) => e.stopPropagation()}
                className="modal-close-button"
              >
                <i className="fa-solid fa-xmark" />
              </button>
            </div>
          )}

          {/* Body */}
          <div className="modal-body">
            {children}
          </div>

          {/* Sticky Footer (custom) */}
          {stickyFooter && (
            <div className="modal-sticky-footer">
              {stickyFooter}
            </div>
          )}

          {/* Footer (buttons) */}
          {buttons.length > 0 && (
            <div className="modal-footer">
              {buttons.map((button, index) => (
                <Button
                  key={index}
                  type="button"
                  onClick={button.onClick}
                  disabled={button.disabled}
                  variant={button.variant || 'secondary'}
                  icon={button.icon}
                  loading={button.loading}
                  style={{ flex: 1 }}
                >
                  {button.label}
                </Button>
              ))}
            </div>
          )}
        </div>
      </div>
    </BodyPortal>
  )
}
